'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';

// AdSense policy disallows ad code on screens without publisher content (login,
// account, admin, editors and other utility flows). Card view pages (board,
// preview, slideshow) keep ads because the free plan shows ads on cards.
const EXCLUDED_ROUTES: RegExp[] = [
  /^\/auth(\/|$)/,
  /^\/account(\/|$)/,
  /^\/admin(\/|$)/,
  /^\/create(\/|$)/,
  /^\/bulk-card-order(\/|$)/,
  /^\/invitations\/update(\/|$)/,
  /^\/invitations\/[^/]+\/create(\/|$)/,
  /^\/card\/[^/]+\/(edit|envelope-settings|message|messages|invite)(\/|$)/,
  /^\/videos\/[^/]+\/create(\/|$)/,
  /^\/group-video-cards\/group-videos\/contribute(\/|$)/,
];

// Any of these means a real visitor is engaging with the page.
const ENGAGEMENT_EVENTS = ['scroll', 'pointerdown', 'touchstart', 'keydown', 'mousemove'] as const;

/**
 * Loads AdSense on the visitor's first interaction instead of at page load.
 * The ad auction (and the dozens of third-party cookies it sets) no longer
 * competes with the first paint, and lab tools like Lighthouse — which never
 * interact — measure the page itself. Engaged visitors get ads within moments.
 * Lives in the root layout, so once loaded it stays loaded across navigation.
 */
export default function AdSenseScript() {
  const pathname = usePathname() || '/';
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    if (engaged) return;
    const start = () => setEngaged(true);
    const opts: AddEventListenerOptions = { once: true, passive: true };
    ENGAGEMENT_EVENTS.forEach((e) => window.addEventListener(e, start, opts));
    return () => ENGAGEMENT_EVENTS.forEach((e) => window.removeEventListener(e, start));
  }, [engaged]);

  if (!engaged || EXCLUDED_ROUTES.some((re) => re.test(pathname))) return null;

  return (
    <Script
      async
      src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4928744963222423"
      crossOrigin="anonymous"
      strategy="afterInteractive"
    />
  );
}
